/**
 * cart-drawer.js
 *
 * Shopify Cart API-powered slide-out cart drawer. Replaces the static
 * prototype cart with live data from the storefront `/cart.js` endpoints.
 *
 * Public surface:
 *   - `window.addToCart(formData)`  — POSTs to /cart/add.js, opens the drawer.
 *   - `window.closeCart()`          — closes the drawer (used by ESC handlers).
 *   - DOM events:
 *       `cart:open`    — open the drawer (also re-fetches the cart).
 *       `cart:refresh` — re-fetch the cart without changing visibility.
 *
 * Vanilla ES2017+, no dependencies. Safe to load with `defer`.
 */

/**
 * Escape a string for safe interpolation into innerHTML. Cart line-item
 * data (product titles, variant titles) is merchant-controlled but still
 * untrusted at render time, so all of it is escaped to prevent XSS.
 *
 * @param {unknown} value - Raw value to escape; coerced to a string.
 * @returns {string} HTML-safe text.
 */
function escapeHtml(value) {
  const str = value == null ? '' : String(value);
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

class CartDrawer {
  constructor() {
    this.drawer         = document.getElementById('cartDrawer');
    this.overlay        = document.getElementById('cartOverlay');
    this.toggle         = document.getElementById('cartToggle');
    this.closeBtn       = document.getElementById('cartClose');
    this.itemsContainer = document.getElementById('cartItems');
    this.subtotalEl     = document.getElementById('cartSubtotal');
    this.countEl        = document.getElementById('cartCount');

    // No drawer in the DOM (e.g. checkout pages) — bail out quietly.
    if (!this.drawer) return;

    this.bindEvents();

    // Hydrate the count badge on load so it reflects the real cart state
    // even before the drawer is first opened.
    this.fetchCart();
  }

  /** Fetch the latest cart, then reveal the drawer and lock scroll. */
  open() {
    this.fetchCart().finally(() => {
      this.drawer.classList.add('open');
      if (this.overlay) this.overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
    });
  }

  /** Hide the drawer and restore background scroll. */
  close() {
    this.drawer.classList.remove('open');
    if (this.overlay) this.overlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  bindEvents() {
    if (this.toggle)   this.toggle.addEventListener('click', () => this.open());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay)  this.overlay.addEventListener('click', () => this.close());

    // Expose close globally for ESC / external callers.
    window.closeCart = () => this.close();

    // React to add-to-cart and refresh events dispatched elsewhere.
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.fetchCart());

    // Shared ESC handler closes both the cart and the mobile nav.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
        if (typeof window.closeMobileNav === 'function') window.closeMobileNav();
      }
    });
  }

  /**
   * Fetch the current cart from Shopify and render it.
   * @returns {Promise<void>}
   */
  async fetchCart() {
    try {
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('Cart request failed with status ' + res.status);
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart fetch failed', err);
    }
  }

  /**
   * Render cart contents into the drawer.
   * @param {object} cart - Parsed response from the Shopify Cart API.
   */
  renderCart(cart) {
    // Update the count badge (visible across all pages, not just the drawer).
    if (this.countEl) {
      this.countEl.textContent = cart.item_count;
      this.countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
    }

    if (!this.itemsContainer) return;

    if (cart.item_count === 0) {
      this.itemsContainer.innerHTML = '<p class="cart-empty">Your bag is empty.</p>';
      if (this.subtotalEl) this.subtotalEl.textContent = '';
      return;
    }

    // Build markup with every interpolated value escaped. Numeric fields
    // (quantity, prices) are coerced to numbers before use so they can
    // never carry markup.
    this.itemsContainer.innerHTML = cart.items.map((item) => {
      const key          = escapeHtml(item.key);
      const productTitle = escapeHtml(item.product_title);
      const altTitle     = escapeHtml(item.title);
      const quantity     = Number(item.quantity) || 0;
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? escapeHtml(item.variant_title)
        : '';
      const imageUrl = item.featured_image && item.featured_image.url
        ? escapeHtml(item.featured_image.url)
        : '';

      const imageMarkup = imageUrl
        ? `<img src="${imageUrl}" alt="${altTitle}" width="80" height="100" loading="lazy">`
        : '';

      return `
        <div class="cart-item" data-key="${key}">
          <div class="cart-item-img">${imageMarkup}</div>
          <div class="cart-item-info">
            <p class="cart-item-name">${productTitle}</p>
            <p class="cart-item-variant">${variantTitle}</p>
            <div class="cart-qty">
              <button class="cart-qty-btn" data-action="decrease" data-key="${key}" data-qty="${quantity - 1}" aria-label="Decrease quantity">&minus;</button>
              <span>${quantity}</span>
              <button class="cart-qty-btn" data-action="increase" data-key="${key}" data-qty="${quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <div class="cart-item-price">
            <p>${escapeHtml(this.formatMoney(item.line_price))}</p>
            <button class="cart-remove" data-key="${key}" aria-label="Remove ${productTitle}">&times;</button>
          </div>
        </div>
      `;
    }).join('');

    // Subtotal.
    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.formatMoney(cart.total_price);
    }

    // Wire quantity controls and remove buttons. data-qty is authored by
    // us above (never user input), so parseInt is safe here.
    this.itemsContainer.querySelectorAll('.cart-qty-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.changeQuantity(btn.dataset.key, parseInt(btn.dataset.qty, 10));
      });
    });
    this.itemsContainer.querySelectorAll('.cart-remove').forEach((btn) => {
      btn.addEventListener('click', () => this.changeQuantity(btn.dataset.key, 0));
    });
  }

  /**
   * Change a line item's quantity (0 removes it) and re-render.
   * @param {string} key - The line item key.
   * @param {number} quantity - Desired quantity; negatives are clamped to 0.
   * @returns {Promise<void>}
   */
  async changeQuantity(key, quantity) {
    const safeQty = Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: safeQty })
      });
      if (!res.ok) throw new Error('Cart change failed with status ' + res.status);
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart change failed', err);
    }
  }

  /**
   * Format an integer amount of cents as a currency string.
   * @param {number} cents - Amount in the smallest currency unit.
   * @returns {string} e.g. "$1,299.00".
   */
  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

/**
 * Add a line item to the cart via the Shopify Cart API, then open the drawer.
 *
 * @param {object} formData - Payload for /cart/add.js (e.g. { id, quantity }).
 * @returns {Promise<object>} The added line item(s) as returned by Shopify.
 * @throws {Error} If the request fails; the message is taken from Shopify's
 *                 `description` when available.
 */
async function addToCart(formData) {
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(formData)
    });

    if (!res.ok) {
      let message = 'Could not add to cart';
      try {
        const err = await res.json();
        if (err && err.description) message = err.description;
      } catch (parseErr) {
        // Response had no JSON body; fall back to the generic message.
      }
      throw new Error(message);
    }

    const result = await res.json();
    document.dispatchEvent(new Event('cart:open'));
    return result;
  } catch (err) {
    console.error('Add to cart failed', err);
    throw err;
  }
}

window.addToCart = addToCart;

document.addEventListener('DOMContentLoaded', () => new CartDrawer());
